import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { Content, Header, Link, Page, Progress, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { QRCodeSVG } from 'qrcode.react';

const SLUG = 'github.com/project-slug';

type Build = {
  app: string;
  repo: string;
  environment: string;
  platform: 'android' | 'ios';
  artifactId: number;
  runNumber: number;
  runUrl: string;
  branch: string;
  commit: string;
  createdAt: string;
  expiresAt: string;
  installable: boolean;
};
type Data = { configured: boolean; refreshedAt?: string; errors: string[]; builds: Build[] };
type QrLink = { build: Build; url: string; expiresAt: string };

const date = (iso: string) => new Date(iso).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' });

// Por qué no hay QR: en prod va a las tiendas; fuera de prod, falta el bundle id de iOS.
const notInstallable = (b: Build) => {
  if (b.environment !== 'prod') return 'Falta iosBundleId';
  return b.platform === 'ios' ? 'TestFlight' : 'Google Play (interno)';
};

/** Tabla de builds con QR; con repo, solo las de esa app. */
const MobileBuildsTable = ({ repo }: { repo?: string }) => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [data, setData] = useState<Data>();
  const [error, setError] = useState<string>();
  const [link, setLink] = useState<QrLink>();

  useEffect(() => {
    (async () => {
      const res = await fetch(`${await discovery.getBaseUrl('mobile-builds')}/builds`);
      if (!res.ok) throw new Error(`El backend respondió ${res.status}`);
      setData(await res.json());
    })().catch(e => setError(String(e?.message ?? e)));
  }, [discovery, fetch]);

  const openQr = async (build: Build) => {
    setError(undefined);
    const res = await fetch(`${await discovery.getBaseUrl('mobile-builds')}/links`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ artifactId: build.artifactId }),
    });
    if (!res.ok) {
      setError(`No se pudo generar el enlace (${res.status})`);
      return;
    }
    setLink({ build, ...(await res.json()) });
  };

  const columns: TableColumn<Build>[] = [
    { title: 'App', field: 'app', defaultGroupOrder: 0 },
    { title: 'Ambiente', field: 'environment' },
    { title: 'Plataforma', field: 'platform', render: b => (b.platform === 'ios' ? 'iOS (IPA)' : 'Android (APK)') },
    { title: 'Build', field: 'runNumber', render: b => <Link to={b.runUrl}>#{b.runNumber}</Link> },
    { title: 'Rama', render: b => `${b.branch} (${b.commit})` },
    { title: 'Fecha', field: 'createdAt', render: b => date(b.createdAt) },
    { title: 'Disponible hasta', field: 'expiresAt', render: b => date(b.expiresAt) },
    {
      title: 'Instalar',
      sorting: false,
      render: b =>
        b.installable ? (
          <Button size="small" variant="outlined" color="primary" onClick={() => openQr(b)}>
            QR
          </Button>
        ) : (
          <Typography variant="caption" color="textSecondary">
            {notInstallable(b)}
          </Typography>
        ),
    },
  ];

  const builds = (data?.builds ?? []).filter(b => !repo || b.repo === repo);

  return (
    <>
      {error && <WarningPanel title="Builds móviles" message={error} />}
      {data && !data.configured && <WarningPanel title="Sin configurar" message="Falta mobileBuilds en el appConfig de Argo." />}
      {data?.errors.map(e => <WarningPanel key={e} severity="info" title="No se pudo leer una app" message={e} />)}
      {!data && !error && <Progress />}
      {data && (
        <Table<Build>
          title={data.refreshedAt ? `Actualizado ${date(data.refreshedAt)}` : 'Primera lectura en curso (cada 15 min)'}
          options={{ paging: false, grouping: !repo, search: !repo, padding: 'dense' }}
          columns={repo ? columns.slice(1) : columns}
          data={builds}
          emptyContent={
            <div style={{ padding: 16 }}>
              {repo ? `No hay builds vigentes de ${repo} (o el repo no está en mobileBuilds.apps).` : 'No hay builds vigentes.'}
            </div>
          }
        />
      )}
      <Dialog open={!!link} onClose={() => setLink(undefined)}>
        {link && (
          <>
            <DialogTitle>
              {link.build.app} · {link.build.environment} · {link.build.platform === 'ios' ? 'iOS' : 'Android'} #{link.build.runNumber}
            </DialogTitle>
            <DialogContent style={{ textAlign: 'center' }}>
              <QRCodeSVG value={link.url} size={240} includeMargin />
              <Typography variant="body2">
                Escanéalo con el teléfono. Caduca a las {new Date(link.expiresAt).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}.
              </Typography>
              {link.build.platform === 'ios' && (
                <Typography variant="caption" color="textSecondary" component="p">
                  En iPhone solo instala si el dispositivo está registrado en el perfil de desarrollo.
                </Typography>
              )}
              <Link to={link.url}>Abrir enlace</Link>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setLink(undefined)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

/** Última build instalable de cada app móvil por ambiente, con QR para instalarla en el teléfono. */
export const MobileBuildsPage = () => (
  <Page themeId="tool">
    <Header title="Builds móviles" subtitle="Última compilación de cada app por ambiente (artefactos de GitHub Actions, 7 días)" />
    <Content>
      <MobileBuildsTable />
    </Content>
  </Page>
);

/** Pestaña Builds en la ficha de una app móvil. */
export const MobileBuildsContent = () => {
  const { entity } = useEntity();
  return <MobileBuildsTable repo={entity.metadata.annotations?.[SLUG]} />;
};

export const isMobileApp = (entity: Entity) =>
  entity.spec?.type === 'mobile-app' && Boolean(entity.metadata.annotations?.[SLUG]);
